import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AiDraft from "@/components/partner/AiDraft";
import { errMsg, pkr } from "@/lib/api";

function Broadcasts({ data, api, roster, reload }) {
  const l = data.listing;
  const [m, setM] = useState({ kind: "logistics", title: "", body: "" });
  const fill = (t) => setM({ kind: t.kind, title: t.title, body: t.body.replace("{meeting_point}", l.meeting_point || "the meeting point").replace("{support_phone}", l.support_phone || "") });
  const send = async () => { try { await api.post(`/partner/listings/${l.id}/broadcasts`, m); toast.success("Posted to every guest portal"); setM({ kind: "logistics", title: "", body: "" }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="broadcasts-panel">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">{data.templates.map((t) => <Button key={t.key} size="sm" variant="outline" className="rounded-full" onClick={() => fill(t)} data-testid={`template-${t.key}`}>{t.title}</Button>)}</div>
        <Select value={m.kind} onValueChange={(v) => setM({ ...m, kind: v })}>
          <SelectTrigger data-testid="broadcast-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="logistics">Logistics</SelectItem><SelectItem value="general">General</SelectItem><SelectItem value="emergency">Emergency</SelectItem></SelectContent>
        </Select>
        <Input placeholder="Title" value={m.title} onChange={(e) => setM({ ...m, title: e.target.value })} data-testid="broadcast-title" />
        <Textarea rows={4} placeholder="Message" value={m.body} onChange={(e) => setM({ ...m, body: e.target.value })} data-testid="broadcast-body" />
        <AiDraft kind="message" context={() => `Listing: ${l.title}. Message goal: ${m.title || "logistics update"}. Notes: ${m.body}`} onApply={(t) => setM({ ...m, body: t })} applyLabel="Use as message" />
        <Button className="rounded-full" disabled={!m.title.trim() || !m.body.trim()} onClick={send} data-testid="broadcast-send-btn">Post to guests (in-app)</Button>
      </div>
      <div className="space-y-3">
        <div className="eyebrow text-muted-foreground">Sent</div>
        {roster.communications.map((c) => (
          <div key={c.id} className="rounded-lg border border-border bg-white p-3 text-sm" data-testid="sent-broadcast"><div className="flex items-center gap-2 font-medium">{c.title}<Badge variant={c.kind === "emergency" ? "destructive" : "outline"}>{c.kind}</Badge></div><p className="mt-1 text-muted-foreground">{c.body}</p></div>
        ))}
      </div>
    </div>
  );
}

function Completion({ data, api, roster, reload }) {
  const partnerEv = roster.evidence.find((e) => e.source === "partner");
  const guestEv = roster.evidence.filter((e) => e.source === "guest" && e.delivered);
  const [note, setNote] = useState(partnerEv?.note || "");
  const [count, setCount] = useState(partnerEv?.attendance_count || 0);
  const send = async () => { try { await api.post(`/partner/listings/${data.listing.id}/completion`, { note, attendance_count: Number(count) }); toast.success("Completion report submitted"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-2xl space-y-4" data-testid="completion-panel">
      <p className="text-sm text-muted-foreground">Payout releases only after mixed verification: your completion report plus at least one guest confirmation, then GOSHA ops approval.</p>
      <div className="flex gap-6 text-sm"><span>Partner report: <b data-testid="evidence-partner">{partnerEv ? "submitted" : "missing"}</b></span><span>Guest confirmations: <b data-testid="evidence-guests">{guestEv.length}</b></span></div>
      <Textarea rows={4} placeholder="How was the experience delivered? Attendance, incidents, deviations." value={note} onChange={(e) => setNote(e.target.value)} data-testid="completion-note" />
      <Input type="number" className="w-40" placeholder="Attendance" value={count} onChange={(e) => setCount(e.target.value)} data-testid="completion-attendance" />
      <Button className="rounded-full" disabled={!note.trim()} onClick={send} data-testid="completion-submit-btn">Submit completion report</Button>
    </div>
  );
}

export default function OpsTab({ data, api, section }) {
  const l = data.listing;
  const [roster, setRoster] = useState(null);
  const load = useCallback(() => api.get(`/partner/listings/${l.id}/roster`).then((r) => setRoster(r.data)).catch((e) => toast.error(errMsg(e))), [api, l.id]);
  useEffect(() => { load(); }, [load]);
  const offer = async (id) => { try { await api.post(`/partner/listings/${l.id}/waitlist/${id}/offer`); toast.success("Marked as offered"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!roster) return null;
  if (section === "broadcasts") return <Broadcasts data={data} api={api} roster={roster} reload={load} />;
  if (section === "completion") return <Completion data={data} api={api} roster={roster} reload={load} />;
  return (
    <div className="space-y-10" data-testid="roster-panel">
      <div className="overflow-x-auto rounded-xl border border-border bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Booking</TableHead><TableHead>Type</TableHead><TableHead>Guests</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {roster.bookings.length === 0 && <TableRow><TableCell colSpan={5} className="text-muted-foreground">No bookings yet.</TableCell></TableRow>}
            {roster.bookings.map((b) => (
              <TableRow key={b.id} data-testid={`roster-row-${b.id}`}>
                <TableCell className="font-medium">{b.code}<div className="text-xs text-muted-foreground">{b.buyer_email}</div></TableCell>
                <TableCell className="capitalize">{b.kind} · {b.seats}</TableCell>
                <TableCell className="text-sm">{b.guests.length ? b.guests.map((g) => g.name).join(", ") : <span className="text-muted-foreground">Not assigned yet</span>}</TableCell>
                <TableCell>{pkr(b.total)}</TableCell>
                <TableCell><Badge variant={b.status === "confirmed" ? "default" : "outline"}>{b.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div>
        <h3 className="text-xl text-primary">Waitlist</h3>
        <div className="mt-3 space-y-2">
          {roster.waitlist.length === 0 && <p className="text-sm text-muted-foreground">Nobody waiting.</p>}
          {roster.waitlist.map((w) => (
            <div key={w.id} className="flex items-center justify-between rounded-lg border border-border bg-white p-3 text-sm" data-testid={`waitlist-row-${w.id}`}>
              <span>{w.name} · {w.email}</span>
              {w.status === "offered" ? <Badge variant="outline">Offered</Badge> : <Button size="sm" variant="outline" className="rounded-full" onClick={() => offer(w.id)} data-testid={`waitlist-offer-${w.id}`}>Offer spot</Button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
